// app/components/Plugins/Bots/BotForm.tsx
'use client'

import React, { useState, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { Bot, Condition, Frequency } from './types'
import { v4 as uuidv4 } from 'uuid'

/**
 * @uiComponent
 * @name BotForm
 * @description The BotForm component provides a comprehensive interface for creating automated trading bots.
 * It allows users to configure bot parameters including trading symbols, exchanges, timeframes, technical indicators,
 * conditions, risk management rules, and notification settings. Features support for multiple exchanges and popular cryptocurrencies.
 * @location Bot creation form in the Bots plugin
 * @prop {function} onSuccess - Optional callback function called when bot is successfully created
 * @prop {function} onClose - Optional callback function called when form is closed
 */

/**
 * @uiWorkflow
 * @name Create Basic Trading Bot
 * @description How to set up an automated trading bot with basic conditions
 * @platform bots
 * @step 1 Open the Bot creation form [component: BotForm] [action: navigate]
 * @step 2 Enter a descriptive bot name [component: BotForm] [action: type] [notes: Choose a memorable name]
 * @step 3 Select trading symbol from popular options [component: BotForm] [action: select] [notes: BTCUSDT, ETHUSDT, etc.]
 * @step 4 Choose your preferred exchange [component: BotForm] [action: select] [notes: Binance, Coinbase, Kraken, Bybit]
 * @step 5 Set the analysis timeframe [component: BotForm] [action: select] [notes: 1m, 5m, 15m, 1h, 1d]
 * @step 6 Configure the first trading condition [component: BotForm] [action: edit] [notes: Indicator, value, type, action]
 * @step 7 Add a second condition if needed [component: BotForm] [action: click] [notes: Optional additional condition]
 * @step 8 Set risk management rules [component: BotForm] [action: type] [notes: Stop loss, position sizing]
 * @step 9 Configure notification preferences [component: BotForm] [action: setup] [notes: Webhook or email alerts]
 * @step 10 Submit the bot configuration [component: BotForm] [action: click] [notes: Click Create Bot button]
 */

/**
 * @uiWorkflow
 * @name Configure Advanced Bot Conditions
 * @description How to set up complex trading conditions with multiple indicators
 * @platform bots
 * @step 1 Start with basic bot setup [component: BotForm] [action: prerequisite] [notes: Name, symbol, exchange, timeframe]
 * @step 2 Focus on the first condition section [component: BotForm] [action: locate]
 * @step 3 Select indicator type [component: BotForm] [action: choose] [notes: Price, RSI, MACD, volume, etc.]
 * @step 4 Set the target value or threshold [component: BotForm] [action: input] [notes: Numeric value for comparison]
 * @step 5 Choose condition type [component: BotForm] [action: select] [notes: crosses, greater_than, less_than, equals]
 * @step 6 Set the action to take [component: BotForm] [action: select] [notes: alert, buy, sell]
 * @step 7 Click "Add Condition" for second condition [component: BotForm] [action: click]
 * @step 8 Configure the second condition similarly [component: BotForm] [action: repeat]
 * @step 9 Test condition logic before saving [component: BotForm] [action: validate]
 */

/**
 * @uiWorkflow
 * @name Set Up Bot Notifications and Risk Management
 * @description How to configure alerts and risk controls for your trading bot
 * @platform bots
 * @step 1 Complete basic bot and condition setup [component: BotForm] [action: prerequisite]
 * @step 2 Scroll to Risk Management section [component: BotForm] [action: navigate]
 * @step 3 Enter risk management rules [component: BotForm] [action: type] [notes: Stop loss %, position size limits]
 * @step 4 Set execution frequency [component: BotForm] [action: select] [notes: always, once_per_day, once_per_hour]
 * @step 5 Configure webhook URL if using external alerts [component: BotForm] [action: input] [notes: Optional webhook endpoint]
 * @step 6 Enter email address for notifications [component: BotForm] [action: type] [notes: Optional email alerts]
 * @step 7 Review all settings before submission [component: BotForm] [action: verify]
 * @step 8 Submit the complete bot configuration [component: BotForm] [action: submit]
 * @step 9 Confirm bot creation success [component: BotForm] [action: observe] [notes: Success callback triggered]
 */

const defaultCondition: Condition = {
  indicator: 'price',
  value: 0,
  type: 'crosses',
  action: 'alert'
}

interface BotFormProps {
  onSuccess?: () => void
  onClose?: () => void
}

// BotForm component
const BotForm = ({ onSuccess, onClose }: BotFormProps) => {
  // Basic fields
  const [botName, setBotName] = useState('')
  const [symbol, setSymbol] = useState('BTCUSDT')
  const [exchange, setExchange] = useState('binance')
  const [timeFrame, setTimeFrame] = useState('1m')
  
  // Popular trading symbols
  const popularSymbols = [
    'BTCUSDT', 'ETHUSDT', 'BNBUSDT', 'ADAUSDT', 'SOLUSDT',
    'XRPUSDT', 'DOTUSDT', 'DOGEUSDT', 'AVAXUSDT', 'MATICUSDT',
    'LINKUSDT', 'LTCUSDT', 'UNIUSDT', 'ATOMUSDT', 'FILUSDT'
  ]
  
  // Supported exchanges
  const exchanges = ['binance', 'coinbase', 'kraken', 'bybit']
  
  // Available timeframes
  const timeFrames = ['1m', '5m', '15m', '30m', '1h', '4h', '1d', '1w']

  // For two conditions, we can maintain an array. Second condition is optional.
  const [conditions, setConditions] = useState<Condition[]>([
    { ...defaultCondition }
  ])

  // Other bot settings
  const [riskManagement, setRiskManagement] = useState('')
  const [frequency, setFrequency] = useState<Frequency>('always')
  const [webhook, setWebhook] = useState('')
  const [email, setEmail] = useState('')
  
  // State to track if component is mounted (for portal)
  const [mounted, setMounted] = useState(false)
  
  // Set mounted state after component mounts
  useEffect(() => {
    setMounted(true)
    return () => setMounted(false)
  }, [])

  // Handle updates to a condition by index
  const updateCondition = (
    index: number,
    field: keyof Condition,
    value: any
  ) => {
    const updated = conditions.map((cond, idx) =>
      idx === index ? { ...cond, [field]: value } : cond
    )
    setConditions(updated)
  }

  // Add a second condition if not already added
  const addCondition = () => {
    if (conditions.length < 2) {
      setConditions([...conditions, { ...defaultCondition }])
    }
  }

  // Submit the bot form
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    const bot: Bot = {
      id: uuidv4(),
      botName,
      symbol,
      exchange,
      timeFrame,
      conditions,
      riskManagement,
      frequency,
      webhook: webhook || undefined,
      email: email || undefined,
      active: true,
      createdAt: new Date().toISOString(),
      triggerCount: 0
    }
    const response = await fetch('/api/bots', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(bot)
    })
    if (response.ok) {
      console.log('Bot created and activated!')
      // Call the onSuccess callback if provided
      if (onSuccess) {
        onSuccess()
      }
    }
  }

  // Form content
  const formContent = (
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
      onClick={onClose}
    >
      <div 
        className="bg-[#88a1ac] p-6 rounded-lg shadow-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold text-[#000000]">Create Trading Bot</h2>
          <button 
            onClick={onClose} 
            className="text-2xl leading-none text-[#000000] hover:text-gray-700"
          >
            &times;
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className="space-y-4 text-[#000000]">
          <div>
            <label className="block text-sm font-medium mb-1">Bot Name:</label>
            <input
              type="text"
              value={botName}
              onChange={e => setBotName(e.target.value)}
              required
              className="w-full p-2 border rounded"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Symbol:</label>
            <select
              value={symbol}
              onChange={e => setSymbol(e.target.value)}
              required
              className="w-full p-2 border rounded"
            >
              {popularSymbols.map(sym => (
                <option key={sym} value={sym}>{sym}</option>
              ))}
              <option value="custom">Custom Symbol...</option>
            </select>
            {symbol === 'custom' && (
              <input
                type="text"
                placeholder="Enter custom symbol (e.g. ADAUSDT)"
                onChange={e => setSymbol(e.target.value.toUpperCase())}
                className="w-full p-2 border rounded mt-2"
                required
              />
            )}
            <div>
              <label className="block text-sm font-medium mb-1">Exchange:</label>
              <select
                value={exchange}
                onChange={e => setExchange(e.target.value)}
                required
                className="w-full p-2 border rounded"
              >
                {exchanges.map(ex => (
                  <option key={ex} value={ex}>{ex.charAt(0).toUpperCase() + ex.slice(1)}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Time Frame:</label>
              <select
                value={timeFrame}
                onChange={e => setTimeFrame(e.target.value)}
                required
                className="w-full p-2 border rounded"
              >
                {timeFrames.map(tf => (
                  <option key={tf} value={tf}>{tf}</option>
                ))}
              </select>
            </div>
          </div>
          {conditions.map((cond, idx) => (
            <div key={idx} className="border p-2 rounded">
              <h4>Condition {idx + 1}</h4>
              <div>
                <label className="block text-sm font-medium mb-1">Indicator:</label>
                <select
                  value={cond.indicator}
                  onChange={e =>
                    updateCondition(idx, 'indicator', e.target.value as any)
                  }
                  className="w-full p-2 border rounded"
                >
                  <option value="price">Price</option>
                  <option value="rsi">RSI</option>
                  <option value="volume">Volume</option>
                  <option value="ma">Moving Average (MA)</option>
                  <option value="ema">Exponential Moving Average (EMA)</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Value:</label>
                <input
                  type="number"
                  value={cond.value}
                  onChange={e =>
                    updateCondition(idx, 'value', parseFloat(e.target.value))
                  }
                  required
                  className="w-full p-2 border rounded"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Type:</label>
                <select
                  value={cond.type}
                  onChange={e =>
                    updateCondition(idx, 'type', e.target.value as any)
                  }
                  className="w-full p-2 border rounded"
                >
                  <option value="crosses">Crosses</option>
                  <option value="cross above">Cross Above</option>
                  <option value="cross below">Cross Below</option>
                  <option value="greater than">Greater Than</option>
                  <option value="less than">Less Than</option>
                  <option value="equals">Equals</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Action:</label>
                <select
                  value={cond.action}
                  onChange={e =>
                    updateCondition(idx, 'action', e.target.value as any)
                  }
                  className="w-full p-2 border rounded"
                >
                  <option value="alert">Alert</option>
                  <option value="buy">Buy</option>
                  <option value="sell">Sell</option>
                  <option value="buy limit">Buy Limit</option>
                  <option value="sell limit">Sell Limit</option>
                  <option value="stop loss">Stop Loss</option>
                  <option value="take profit">Take Profit</option>
                </select>
              </div>
              {['buy limit', 'sell limit'].includes(cond.action as string) && (
                <div>
                  <label className="block text-sm font-medium mb-1">Limit Price:</label>
                  <input
                    type="number"
                    value={cond.limitPrice || 0}
                    onChange={e =>
                      updateCondition(idx, 'limitPrice', parseFloat(e.target.value))
                    }
                    required
                    className="w-full p-2 border rounded"
                  />
                </div>
              )}
            </div>
          ))}
          {conditions.length < 2 && (
            <button 
              type="button" 
              onClick={addCondition}
              className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
            >
              Add Second Condition (Optional)
            </button>
          )}
          <div>
            <label className="block text-sm font-medium mb-1">Risk Management:</label>
            <input
              type="text"
              value={riskManagement}
              onChange={e => setRiskManagement(e.target.value)}
              className="w-full p-2 border rounded"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Frequency:</label>
            <select
              value={frequency}
              onChange={e => setFrequency(e.target.value as Frequency)}
              className="w-full p-2 border rounded"
            >
              <option value="always">Always (restart after trigger)</option>
              <option value="once">Once (stop after one trigger)</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Webhook URL:</label>
            <input
              type="url"
              value={webhook}
              onChange={e => setWebhook(e.target.value)}
              className="w-full p-2 border rounded"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Email (for notifications):</label>
            <input
              type="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              className="w-full p-2 border rounded"
            />
          </div>
          <button 
            type="submit"
            className="w-full px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600 font-medium"
          >
            Create Bot
          </button>
        </form>
      </div>
    </div>
  )
  
  // Only render the portal on the client-side after component has mounted
  return mounted ? createPortal(formContent, document.body) : null
}

export default BotForm
